import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { HelpCircle, ChevronLeft, ChevronRight, GraduationCap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { HELP_CONTENT, TUTORIAL_STEPS } from "@/lib/helpContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingHelp() {
  const location = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Abre automaticamente só no primeiro acesso do IP registrado.
    axios.get(`${API}/tutorial/status`)
      .then((r) => { if (!r.data?.seen) setTutorialOpen(true); })
      .catch(() => {});
  }, []);

  const current = (() => {
    const p = location.pathname;
    if (HELP_CONTENT[p]) return HELP_CONTENT[p];
    const keys = Object.keys(HELP_CONTENT)
      .filter((k) => k !== "/" && p.startsWith(k))
      .sort((a, b) => b.length - a.length);
    return keys[0] ? HELP_CONTENT[keys[0]] : HELP_CONTENT["/"];
  })();

  const fecharTutorial = () => {
    setTutorialOpen(false);
    axios.post(`${API}/tutorial/seen`).catch(() => {});
    setStep(0);
  };
  const abrirTutorial = () => {
    setStep(0);
    setHelpOpen(false);
    setTutorialOpen(true);
  };

  const s = TUTORIAL_STEPS[step];
  const ultimo = step === TUTORIAL_STEPS.length - 1;

  return (
    <>
      <button
        onClick={() => setHelpOpen(true)}
        data-testid="help-button"
        className="fixed bottom-5 right-5 z-40 w-12 h-12 rounded-full bg-slate-900 text-white shadow-lg flex items-center justify-center hover:bg-slate-800 hover:scale-105 transition-all"
        title="Ajuda desta aba"
        aria-label="Ajuda desta aba"
      >
        <HelpCircle className="w-6 h-6" />
      </button>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-slate-500" />
              {current.title}
            </DialogTitle>
            <DialogDescription className="text-left text-slate-600 text-sm leading-relaxed pt-2 whitespace-pre-line">
              {current.desc}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Button variant="outline" className="w-full" onClick={abrirTutorial} data-testid="help-rever-tutorial">
              <GraduationCap className="w-4 h-4 mr-2" /> Ver tutorial completo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={tutorialOpen} onOpenChange={(o) => { if (!o) fecharTutorial(); }}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="tutorial-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-400 mb-1">
              <GraduationCap className="w-4 h-4" /> Passo {step + 1} de {TUTORIAL_STEPS.length}
            </div>
            <DialogTitle data-testid="tutorial-step-title">{s.title}</DialogTitle>
            <DialogDescription className="text-left text-slate-600 text-sm leading-relaxed pt-2 whitespace-pre-line">
              {s.desc}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-1.5 my-2">
            {TUTORIAL_STEPS.map((etapa, i) => (
              <span key={etapa.title} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-slate-900" : "w-1.5 bg-slate-200"}`} />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 mt-2">
            <Button variant="ghost" onClick={fecharTutorial} data-testid="tutorial-skip">Pular</Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" disabled={step === 0} onClick={() => setStep((x) => Math.max(0, x - 1))} data-testid="tutorial-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {!ultimo ? (
                <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setStep((x) => Math.min(TUTORIAL_STEPS.length - 1, x + 1))} data-testid="tutorial-next">
                  Próximo <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={fecharTutorial} data-testid="tutorial-finish">
                  Concluir
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
